import { Container } from "pixi.js"

import { prefersReducedMotion } from "../../intro/loading_handoff"
import { ART_PX } from "../../intro/pixel_raster"
import { MIRROR_AXIS, REST } from "../../intro/shipwreck_geometry"
import { StormNight } from "../../intro/storm_night"
import { CreationLayout } from "./creation_layout"

// The login intro's storm, behind the ship's rail: the same sky torn open round the moon, the heaving sea,
// the pine headland, the rain and the lightning, from the intro's own painters (StormNight). The ship herself
// is not drawn -- the player is standing on her.
//
// Laid out once per layout. Each frame the storm repaints only what has changed, and the whole night rises
// and falls behind the rail: the ship labouring in the sea.

/** The heave behind the rail: a slow rise and fall, a couple of art pixels either way. */
const BOB_MS = 1100
const BOB_PX = 2
/** The storm's clock when the page opens: past the login's opening strikes, a few seconds before the next. */
const STORM_FROM = REST + 3700

export class NightSky extends Container {
    private readonly storm = new StormNight(!prefersReducedMotion())
    private offset = { dx: 0, dy: 0 }

    constructor() {
        super()
        this.addChild(this.storm.back, this.storm.front)
    }

    /**
     * Lay the storm out for the page's view, a few rows taller than it so the heave never shows an edge. On a
     * phone it is moved (layout.night) so the horizon and the moon still show above the rail; the scene is
     * authored mirrored, so the rectangle it is laid out for is the view's mirror image.
     */
    fit = (layout: CreationLayout) => {
        const { dx, dy } = layout.night
        this.offset = { dx, dy }
        const v = layout.view
        this.x = dx * ART_PX
        this.storm.build({
            left: 2 * MIRROR_AXIS - (v.x + v.w - dx) * ART_PX,
            right: 2 * MIRROR_AXIS - (v.x - dx) * ART_PX,
            top: (v.y - dy) * ART_PX - 8,
            bottom: (v.y + v.h - dy) * ART_PX + 8,
        })
    }

    /** The storm at `ms` into the page: on the intro's clock, which runs at half speed. */
    animate = (ms: number) => {
        this.y = (this.offset.dy + Math.round(BOB_PX * Math.sin(ms / BOB_MS))) * ART_PX
        this.storm.paint(STORM_FROM + ms * 0.5)
    }

    dispose = () => {
        this.storm.dispose()
    }
}
